import { useCallback, useEffect, useRef } from 'react';

import type { CloneController, CloneField, CloneRow, CloneType, ResolvedCloneRows } from '../assistant/cloneActions';
import { drainCrudClone, registerCloneController, unregisterCloneController } from '../assistant/cloneRegistry';

import { useFeatureToggle } from './useFeatureToggle';

/** How often a mounted list looks again for a proposal its rows were not ready for. */
const DRAIN_RETRY_MS = 100;

/** What a list hands over so the assistant can clone from it. */
export interface CrudCloneSource {
  /** The clone table's own columns, and what may be done to each. */
  fields: readonly CloneField[];
  /** The rows for these keys, or the user's current selection when none are named. */
  resolveRows: (keys: readonly string[]) => ResolvedCloneRows;
  openWith: (rows: CloneRow[], originals: CloneRow[], cloneType: CloneType) => void;
}

/**
 * Lets the assistant open this list's clone table, carrying the changes it proposes, while the list is
 * mounted.
 *
 * The list passes the columns its clone actually has, how to resolve the rows it holds, and the opener; all
 * three are read through this on every attempt, so what answers is the list as it is now. A proposal that
 * arrived while the user was elsewhere is drained here as the list comes up, retrying rather than running
 * once, because a list mounts before its rows have loaded.
 *
 * Gated on `enableAssistant`, like the page context: with the flag off nothing is registered, so a tenant
 * without Ohm has no list the assistant can clone from.
 */
export const useCrudClone = (entityType: string | undefined, source: CrudCloneSource): void => {
  const enabled = useFeatureToggle(['enableAssistant']) && entityType !== undefined;

  // Read at call time rather than captured, so a fresh proposal reaches the list's current rows and opener,
  // and the registration does not churn on every render the list does.
  const current = useRef(source);
  current.current = source;

  const readController = useCallback(
    (): CloneController => ({
      fields: () => [...current.current.fields],
      rows: keys => current.current.resolveRows(keys),
      open: (rows, originals, cloneType) => current.current.openWith(rows, originals, cloneType),
    }),
    [],
  );

  useEffect(() => {
    if (!enabled || entityType === undefined) return;

    registerCloneController(entityType, readController);

    let retry: ReturnType<typeof setTimeout> | undefined;
    const drain = (): void => {
      if (drainCrudClone(entityType, readController)) return;
      retry = setTimeout(drain, DRAIN_RETRY_MS);
    };
    drain();

    return () => {
      clearTimeout(retry);
      unregisterCloneController(entityType, readController);
    };
  }, [enabled, entityType, readController]);
};
